import cx from 'classnames'
import { PropsWithChildren } from 'react'
import styles from './Text.module.css'

export type TextVariant =
  // App variants
  | 'page-title'
  | 'section-title'
  | 'section-header'
  | 'invoice-number'
  | 'amount'
  | 'body'
  | 'date'
  | 'field-label'
  // Content variants
  | 'content-hero-supporting'
  | 'content-section-supporting'

type TextElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'code'

const defaultElements: Record<TextVariant, TextElement> = {
  'page-title': 'h1',
  'section-title': 'h2',
  'section-header': 'h3',
  'invoice-number': 'span',
  amount: 'span',
  body: 'p',
  date: 'span',
  'field-label': 'label',
  'content-hero-supporting': 'p',
  'content-section-supporting': 'p',
}

interface TextProps {
  /**
   * The text style variant
   *
   * @default 'body'
   */
  variant?: TextVariant
  /** Override the default HTML element */
  component?: TextElement
  /** Additional CSS class */
  className?: string
}

export function Text({
  children,
  variant = 'body',
  component,
  className,
  ...props
}: PropsWithChildren<TextProps>) {
  const Component = component ?? defaultElements[variant]
  return (
    <Component
      {...props}
      className={cx(styles.Text, className, {
        [styles.isPageTitle]: variant === 'page-title',
        [styles.isSectionTitle]: variant === 'section-title',
        [styles.isSectionHeader]: variant === 'section-header',
        [styles.isInvoiceNumber]: variant === 'invoice-number',
        [styles.isAmount]: variant === 'amount',
        [styles.isBody]: variant === 'body',
        [styles.isDate]: variant === 'date',
        [styles.isFieldLabel]: variant === 'field-label',
        [styles.isContentHeroSupporting]:
          variant === 'content-hero-supporting',
        [styles.isContentSectionSupporting]:
          variant === 'content-section-supporting',
      })}
    >
      {children}
    </Component>
  )
}
